/**
 * Host adapter modeli.
 *
 * Yangi super app qo'shish uchun:
 *   1. `HostId` ga yangi kalit qo'shing
 *   2. Auth turiga qarab factory tanlang:
 *      - Cookie/session asosida    → `defineCookieHost`     (Click, …)
 *      - URL query param asosida   → `defineQueryParamHost` (Zoomrad, …)
 *      - URL path segment asosida  → `definePathParamHost`  (Paynet, …)
 *      - Boshqa                    → `HostAdapter` ni to'g'ridan-to'g'ri implement qiling
 *   3. `src/hosts/index.ts` dagi `HOSTS` ro'yxatiga qo'shing
 *   4. `src/config/uiTokens.ts` ichida brand token to'plamini kengaytiring
 */

import type { AppLang } from "@/lib/env";
import type { BrandId } from "@/config/uiTokens";

export type HostId =
  | "click"
  | "telegram"
  | "zoomrad"
  | "payme"
  | "paynet"
  | "anorbank"
  | "web";

export type ThemeMode = "dark" | "light";

export interface LoginResponse {
  token: string;
  phoneNumber: string;
  fullName: string;
}

/**
 * Ko'plab vendor API lari qaytaradigan kengaytirilgan response shakli
 * (Payme, Zoomrad va shunga o'xshash — nested `user` ob'ekti bilan).
 * `mapVendorResponse()` buni standart `LoginResponse` ga aylantiradi.
 */
export interface VendorLoginResponse {
  token: string;
  tokenType: string;
  expiresIn: number;
  isFirstVisited: boolean;
  platform: string;
  /**
   * Ba'zi vendorlar (mas. Paynet) login javobida afzal ko'rilgan mavzuni beradi
   * (`"dark"` | `"light"`). Ixtiyoriy — hostlar buni `onLoginResponse` orqali
   * saqlashi mumkin (`mapVendorResponse` bu maydonni e'tiborsiz qoldiradi).
   */
  theme?: string;
  user: {
    id: number;
    clickClientId: string | null;
    telegramUserId: string | null;
    phoneNumber: string;
    fullName: string | null;
    isIdentified: boolean;
  };
}

/** `VendorLoginResponse` ni `LoginResponse` ga normalize qiladi */
export function mapVendorResponse(raw: VendorLoginResponse): LoginResponse {
  return {
    token: raw.token,
    phoneNumber: raw.user.phoneNumber ?? "",
    fullName: raw.user.fullName ?? "",
  };
}

export interface HostAdapter {
  /** Yagona identifikator — registry da takrorlanmasligi shart */
  readonly id: HostId;

  /** Bu host qaysi brand token to'plamiga bog'langan */
  readonly brandId: BrandId;

  /**
   * Hozirgi runtime shu host ga mosligini sinxron tekshiradi.
   * Asosan domen tekshiruvi yoki sinxron flag uchun. Telegram kabi
   * kechikuvchi signal lar `awaitMatch` da hal qilinadi.
   */
  match(): boolean;

  /**
   * Telegram `initData` yoki Click `web-session` cookie kabi
   * kechikib keluvchi signal lar uchun async tekshiruv.
   * Sync `match()` true qaytarsa bu chaqirilmaydi.
   */
  awaitMatch?(): Promise<boolean>;

  /**
   * Login API ni chaqiradi va tokenni saqlaydi. Host-specific tayyorgarlik
   * (initData kutish, web-session cookie ni kutish, va h.k.) shu ichida bo'ladi.
   */
  login(): Promise<LoginResponse>;

  /**
   * App yopilayotganda (`pagehide` eventi) chaqiriladi — fire-and-forget.
   * Ichida `fetch keepalive` ishlatiladi, Promise qaytarmaydi.
   * Barcha hostlar uchun majburiy emas (ixtiyoriy).
   */
  logout?(): void;

  /** Host taqdim etgan til (mas. Click `click-language` cookie). Yo'q bo'lsa null. */
  getPreferredLang?(): AppLang | null;

  /** Host taqdim etgan tema. Yo'q bo'lsa null. */
  getPreferredTheme?(): ThemeMode | null;
}

/** Host aniqlanmagan yoki qo'llab-quvvatlanmagan muhitda tashlanadi */
export class UnsupportedHostError extends Error {
  constructor(message = "Ilova qo'llab-quvvatlanmagan muhitda ochilgan") {
    super(message);
    this.name = "UnsupportedHostError";
  }
}
